<script setup>
  import { ref, onUnmounted, useTemplateRef, watchEffect, onBeforeUnmount } from 'vue'
  import { useFocusTrap } from '@/composables/useFocusTrap';

  defineOptions({
    inheritAttrs: false
  })  

  const props = defineProps({
    button: {
      type: Object,
      require: true
    },
    positionY: {
      type: String,
      default: 'top'
    },
    positionX: {
      type: String,
      default: 'right'
    },
    popupRect: {
      type: Object,
      required: false
    },
    menuWidth: {
      type: String,
      default: null
    },
    teleport: {
      type: Boolean,
      default: false
    }
  })

  const showMenu = defineModel();

  const popupMenu = useTemplateRef('popup-menu');

  const focusTrap = useFocusTrap(popupMenu);
  
  const state = ref('closed');
  const pixelPosition = ref({ x: 0, y: 0, xOffset: 8, yOffset: 4 });

  function closeMenu() {
    state.value = 'closing';

    popupMenu.value.addEventListener('animationend', handleAnimationEnd);
  }

  function getPixelPositions() {
    const buttonRect = props.button.getBoundingClientRect();
    const pageWidth = document.documentElement.clientWidth;

    if (buttonRect.left - 24 < 0) {
      pixelPosition.value.xOffset = 0;
    } else if (buttonRect.left + buttonRect.width + 24 > pageWidth) {
      pixelPosition.value.xOffset = 0;
    }

    if (props.positionX === 'center') {

      pixelPosition.value.x = 
        props.teleport 
          ? buttonRect.left + (buttonRect.width / 2)
          : buttonRect.width / 2;

    } else if (props.positionX === 'right') {

      pixelPosition.value.x = 
        props.teleport 
          ? buttonRect.left + buttonRect.width 
          : buttonRect.width;

    } else if (props.positionX === 'left') {

      pixelPosition.value.x = 
        props.teleport 
          ? buttonRect.left
          : 0;

    }

    if (props.positionY === 'bottom') {

      pixelPosition.value.y = 
        props.teleport 
          ? buttonRect.top + buttonRect.height 
          : buttonRect.height;

    }
    else if( props.positionY === 'center') {

      pixelPosition.value.y = 
        props.teleport 
          ? buttonRect.top + (buttonRect.height / 2)
          : buttonRect.height / 2;

    }
    else if( props.positionY === 'top') {

      pixelPosition.value.y = 
        props.teleport 
          ? buttonRect.top
          : 0;

    }
  }

  const handleBodyClick = (event) => {
    if (state.value === 'opening') return;
    
    if (popupMenu.value && !popupMenu.value.contains(event.target)) {
      closeMenu();
    }
  }

  function handleAnimationEnd() {
    if (state.value === 'closing') {
      state.value = 'closed';
      showMenu.value = false;
    } else if (state.value === 'opening') {
      state.value = 'open';
      showMenu.value = true;
      focusTrap.activate();
    }
  }
  
  watchEffect(() => {
    if (!popupMenu.value) return;

    getPixelPositions();

    state.value = 'opening';

    document.body.addEventListener('click', handleBodyClick);
    popupMenu.value.addEventListener('animationend', handleAnimationEnd);
  });

  onBeforeUnmount(() => {
    popupMenu.value.removeEventListener('animationend', handleAnimationEnd);
  });

  onUnmounted(() => {
    document.body.removeEventListener('click', handleBodyClick);
  });
</script>

<template>
  <Teleport :disabled="!props.teleport" defer to="body">
    <div
      v-bind="$attrs"
      ref="popup-menu"
      tabindex="-1"
      class="jwx-popup__menu-wrapper"
      :data-state="state"
      :data-position-y="props.positionY"
      :data-position-x="props.positionX"
      :data-teleport="props.teleport"
      :style="{
        top: `${pixelPosition.y}px`,
        left: `${pixelPosition.x}px`,
        '--position-x-offset': `${pixelPosition.xOffset}px`,
        '--position-y-offset': `${pixelPosition.yOffset}px`,
      }"
    >
      <slot name="menu-content"></slot>
    </div>
  </Teleport>
</template>